/* The view-model rules for a PII classification. Pure functions, no React, no fetch.
 *
 * This file exists because the single most dangerous thing this screen can do is render
 * `inconclusive` as though it were `approved`. `inconclusive` means the classifier failed and the
 * PII status is **unknown**; showing it in the same visual language as a human-approved "no PII"
 * verdict is how a column of Aadhaar numbers ends up on a dashboard someone trusts.
 *
 * Keeping the decision here rather than inline in JSX is what makes it testable without a DOM, and
 * `vitest.config` covers `model.ts`/`model/**` at 80% — so the rule is gated, not just written.
 */

import type {
  Classification,
  ClassificationState,
  PiiKind,
} from '@/shared/api/generated/catalog';

/* How a state should read to a human. Deliberately NOT a red/green axis.
 *
 * `unknown` is its own tone rather than a shade of `bad`, because "we could not tell" and "we found
 * PII" call for different actions: the first needs a re-run, the second needs a policy. Collapsing
 * them into one warning colour trains people to treat both as noise. */
export type ReviewTone = 'authoritative' | 'awaiting-human' | 'unknown' | 'superseded';

/* A column and what it was judged to hold.
 *
 * The kind is carried, not just the column name. "pan_number" tells a reviewer which column; "PAN"
 * tells them what the platform thinks is in it, and those are different facts — a column named
 * `notes` judged to hold an Aadhaar number is exactly the case where the name is useless. */
export interface PiiFinding {
  readonly column: string;
  readonly kind: PiiKind;
  /* The i18n key for the kind's label. Never the raw enum value: `card_number` rendered literally is
   * worse than nothing, and a UI that formats it locally is a second translation table. */
  readonly labelKey: string;
}

export interface ReviewRow {
  readonly datasetId: string;
  readonly state: ClassificationState;
  readonly tone: ReviewTone;
  /* Whether a human may accept this verdict now. False for `inconclusive` — approving it would
   * convert "the classifier failed" into "a human says this is fine", which the server refuses with
   * a 409 anyway. Disabling the control means the user is not invited to try. */
  readonly canApprove: boolean;
  /* Whether this row may be rendered as "no PII found".
   *
   * True only for an APPROVED record with zero findings — a human looked and confirmed there is
   * nothing. Every other empty record means nobody looked (`pending`), the look failed
   * (`inconclusive`), or there are findings to show instead.
   *
   * Named for the claim rather than for the state, because the claim is what a reader of the JSX
   * needs: `if (row.claimsNoPii)` is checkable against the screen, `if (row.statesPiiOutcome)` is
   * not — and the earlier version of that predicate was wrong in a way nobody caught until the
   * screen was looked at. */
  readonly claimsNoPii: boolean;
  readonly findings: readonly PiiFinding[];
  readonly detail: string | null;
  readonly approvedBy: string | null;
  readonly checksum: string;
}

const TONES: Readonly<Record<ClassificationState, ReviewTone>> = {
  approved: 'authoritative',
  proposed: 'awaiting-human',
  pending: 'awaiting-human',
  /* Not 'awaiting-human': a stale record had a decision that the schema then moved out from under,
   * so the reviewer needs to see the prior verdict and who made it — a different task from
   * reviewing something nobody has looked at. */
  stale: 'superseded',
  inconclusive: 'unknown',
};

export function piiKindLabelKey(kind: PiiKind): string {
  return `pii.kind.${kind}`;
}

/* Only `approved` is authoritative — a live human decision. Mirrors
 * `ClassificationState.is_authoritative` on the backend, and the mirroring is deliberate: this is a
 * rule about how to *present* a state, and presenting it as anything else would be a second,
 * unchecked policy. */
export function isAuthoritative(state: ClassificationState): boolean {
  return state === 'approved';
}

/* Everything except `approved`. Defined as the complement rather than as a list of states, for the
 * same reason the backend is: a list omitted `proposed` there, which made the approval step
 * unreachable. A state added to the spec later is included here by default, which is the safe
 * direction. */
export function needsReview(state: ClassificationState): boolean {
  return !isAuthoritative(state);
}

/* Approvable only when a verdict exists to approve.
 *
 * `inconclusive` is excluded because there is no verdict — the classifier did not produce one. The
 * server refuses it with a 409; this stops the UI from offering an action that cannot succeed, which
 * is a different and better failure than a rejected request. */
export function canApprove(classification: Classification): boolean {
  return (
    (classification.state === 'proposed' || classification.state === 'stale') &&
    classification.verdicts.length > 0
  );
}

/* Whether this record may be rendered as "no PII found".
 *
 * The guard against the worst rendering bug available here: `verdicts: []` on an `inconclusive`
 * record means "we never looked", and `verdicts: []` on an `approved` record means "we looked and
 * found nothing". A UI reading emptiness alone cannot tell those apart and would show the first as
 * clean.
 *
 * An earlier version of this returned true for `approved` *or* `stale` and was consumed as "does
 * this row show its PII at all", which made a `proposed` record with real findings render as
 * "Not assessed" — hiding from the reviewer the exact thing they were being asked to review. Found
 * by looking at the screen, which is the only place that bug was visible.
 *
 * Now it answers one question only, and findings are shown whenever they exist regardless of state.
 * A clean *claim* still requires a human: only `approved` may produce it. */
export function claimsNoPii(classification: Classification): boolean {
  return classification.state === 'approved' && piiFindings(classification).length === 0;
}

export function piiFindings(classification: Classification): readonly PiiFinding[] {
  return (
    classification.verdicts
      /* `kind: 'none'` is a column the classifier examined and cleared. Counting it as a finding
       * inflates every dataset's total and trains reviewers to ignore the number. */
      .filter((verdict) => verdict.kind !== 'none')
      .map((verdict) => ({
        column: verdict.column_name,
        kind: verdict.kind,
        labelKey: piiKindLabelKey(verdict.kind),
      }))
  );
}

export function toReviewRow(classification: Classification): ReviewRow {
  return {
    datasetId: classification.dataset_id,
    state: classification.state,
    tone: TONES[classification.state],
    canApprove: canApprove(classification),
    claimsNoPii: claimsNoPii(classification),
    findings: piiFindings(classification),
    detail: classification.detail ?? null,
    approvedBy: classification.approved_by ?? null,
    checksum: classification.classified_against,
  };
}

/* Queue order: the states that need the most attention first, then oldest-looking first.
 *
 * `inconclusive` leads because it is the only state where the platform does not know the answer —
 * an unreviewed proposal is a pending decision, but an unreviewed failure is a blind spot. */
const URGENCY: Readonly<Record<ClassificationState, number>> = {
  inconclusive: 0,
  stale: 1,
  proposed: 2,
  pending: 3,
  approved: 4,
};

export function orderForReview(rows: readonly ReviewRow[]): readonly ReviewRow[] {
  return [...rows].sort((left, right) => {
    const byUrgency = URGENCY[left.state] - URGENCY[right.state];
    return byUrgency !== 0 ? byUrgency : left.datasetId.localeCompare(right.datasetId);
  });
}
